import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PatientForm from './components/PatientForm';
import type { PatientFormData } from './components/PatientForm';
import ConsultationForm from './components/ConsultationForm';
import type { ConsultationFormData } from './components/ConsultationForm';
import PrescriptionForm from './components/PrescriptionForm';
import type { PrescriptionFormData } from './components/PrescriptionForm';
import BonLaboratoireForm from './components/BonLaboratoireForm';
import type { BonLaboratoireFormData } from './components/BonLaboratoireForm';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  sexe: string;
  telephone: string;
  email: string;
  adresse: string;
  groupe_sanguin: string;
  allergies: string;
  antecedents_medicaux: string;
  maladies_chroniques: string;
  antecedents_chirurgicaux: string;
  medicaments_actuels: string;
  notes_medicales: string;
  personne_contact_nom: string;
  personne_contact_telephone: string;
  numero_dossier: string;
  is_provisional: boolean;
  created_at: string;
  numero_securite_sociale: string;
  _source: 'patient';
}

interface PatientTemporaire {
  id: string;
  nom: string;
  prenom: string;
  numero_dossier: string;
  import_batch_id: string;
  created_at: string;
  _source: 'temporaire';
}

type SearchResult = Patient | PatientTemporaire;

interface Consultation {
  id: string;
  date_rdv: string;
  heure_debut: string;
  heure_fin: string;
  statut: string;
  motif: string;
  diagnostique: string;
  traitement: string;
  medicaments: string;
  notes_medecin: string;
  type_consultation: string;
  medecin_id: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface Prescription {
  id: string;
  patient_id: string;
  medecin_id: string;
  date_prescription: string;
  diagnostic: string;
  medicaments: unknown;
  instructions: string;
  duree_traitement: string;
  created_at: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface BonLaboratoire {
  id: string;
  patient_id: string;
  medecin_id: string;
  type_analyse: string;
  description: string;
  observations_cliniques: string;
  date_prescription: string;
  statut: string;
  urgences: boolean;
  created_at: string;
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface Suivi {
  id: string;
  patient_id: string;
  medecin_expediteur_id: string;
  medecin_destinataire_id: string;
  objet: string;
  message: string;
  diagnostic: string;
  traitement: string;
  notes_medicales: string;
  statut: string;
  created_at: string;
  medecins_expediteur: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
  medecins_destinataire: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

type PatientTab = 'infos' | 'consultations' | 'prescriptions' | 'bons-labos' | 'suivis' | 'dossier-rose';

const EMPTY_FORM: PatientFormData = {
  nom: '', prenom: '', date_naissance: '', sexe: '', telephone: '', email: '',
  adresse: '', groupe_sanguin: '', allergies: '',
  antecedents_medicaux: '', maladies_chroniques: '', antecedents_chirurgicaux: '',
  medicaments_actuels: '', notes_medicales: '', personne_contact_nom: '',
  personne_contact_telephone: '',
};

export default function GestionPatientsPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedTempPatient, setSelectedTempPatient] = useState<PatientTemporaire | null>(null);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [bonsLabo, setBonsLabo] = useState<BonLaboratoire[]>([]);
  const [suivis, setSuivis] = useState<Suivi[]>([]);
  const [hasDossierRose, setHasDossierRose] = useState(false);
  const [dossierRose, setDossierRose] = useState<Record<string, unknown> | null>(null);
  const [medecins, setMedecins] = useState<Medecin[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<PatientTab>('infos');
  const [formData, setFormData] = useState<PatientFormData>(EMPTY_FORM);
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const [showBonLaboForm, setShowBonLaboForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [correctionNom, setCorrectionNom] = useState('');
  const [correctionPrenom, setCorrectionPrenom] = useState('');
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePatients, setDuplicatePatients] = useState<SearchResult[]>([]);
  const [pendingCreateData, setPendingCreateData] = useState<Record<string, unknown> | null>(null);
  const { mutate, state: mutationState } = useOfflineMutation<any>();
  const [generatedActivationCode, setGeneratedActivationCode] = useState<string | null>(null);
  const [generatedNumeroDossier, setGeneratedNumeroDossier] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'secretaire' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadMedecins();
  }, [user, role, authLoading, navigate]);

  const loadMedecins = async () => {
    const { data } = await supabase.from('medecins').select('id, nom, prenom, specialite').order('nom');
    if (data) setMedecins(data);
  };

  const searchPatients = useCallback(async () => {
    if (!searchTerm.trim()) { setSearchResults([]); return; }
    setLoading(true);
    const term = `%${searchTerm.trim()}%`;

    // Rechercher dans les deux tables en parallèle
    const [realRes, tempRes] = await Promise.all([
      supabase
        .from('patients')
        .select('*')
        .or(`nom.ilike.${term},prenom.ilike.${term},numero_dossier.ilike.${term}`)
        .order('nom')
        .limit(30),
      supabase
        .from('patients_temporaires')
        .select('*')
        .or(`nom.ilike.${term},prenom.ilike.${term},numero_dossier.ilike.${term}`)
        .order('nom')
        .limit(30),
    ]);

    const results: SearchResult[] = [];

    if (realRes.data) {
      for (const p of realRes.data) {
        results.push({ ...p, _source: 'patient' as const });
      }
    }
    if (tempRes.data) {
      for (const p of tempRes.data) {
        results.push({ ...p, _source: 'temporaire' as const });
      }
    }

    // Trier par nom
    results.sort((a, b) => a.nom.localeCompare(b.nom));

    setSearchResults(results);
    setLoading(false);
  }, [searchTerm]);

  const selectPatient = async (result: SearchResult) => {
    setIsCreatingNew(false);
    if (result._source === 'temporaire') {
      // Patient temporaire : afficher le formulaire de complétion
      const tp = result as PatientTemporaire;
      setSelectedPatient(null);
      setSelectedTempPatient(tp);
      setFormData({
        nom: tp.nom || '', prenom: tp.prenom || '', date_naissance: '',
        sexe: '', telephone: '', email: '',
        adresse: '',
        groupe_sanguin: '', allergies: '',
        antecedents_medicaux: '', maladies_chroniques: '',
        antecedents_chirurgicaux: '', medicaments_actuels: '',
        notes_medicales: '', personne_contact_nom: '',
        personne_contact_telephone: '',
      });
      setConsultations([]);
      setPrescriptions([]);
      setBonsLabo([]);
      setSuivis([]);
      setHasDossierRose(false);
      setDossierRose(null);
      setErrorMsg('');
      setSuccessMsg('');
      setActiveTab('infos');
      setLoadingRecords(false);
      return;
    }

    // Patient réel
    const patient = result as Patient;
    setSelectedPatient(patient);
    setSelectedTempPatient(null);
    setFormData({
      nom: patient.nom || '', prenom: patient.prenom || '', date_naissance: patient.date_naissance || '',
      sexe: patient.sexe || '', telephone: patient.telephone || '', email: patient.email || '',
      adresse: patient.adresse || '',
      groupe_sanguin: patient.groupe_sanguin || '', allergies: patient.allergies || '',
      antecedents_medicaux: patient.antecedents_medicaux || '', maladies_chroniques: patient.maladies_chroniques || '',
      antecedents_chirurgicaux: patient.antecedents_chirurgicaux || '', medicaments_actuels: patient.medicaments_actuels || '',
      notes_medicales: patient.notes_medicales || '', personne_contact_nom: patient.personne_contact_nom || '',
      personne_contact_telephone: patient.personne_contact_telephone || '',
    });
    setErrorMsg('');
    setSuccessMsg('');
    setActiveTab('infos');

    await loadAllPatientRecords(patient.id);
  };

  const loadAllPatientRecords = async (patientId: string) => {
    setLoadingRecords(true);
    try {
      const [consultRes, prescRes, bonsRes, suivisRes, dossierRes] = await Promise.all([
        supabase
          .from('rendez_vous')
          .select('*, medecins(nom, prenom, specialite)')
          .eq('patient_id', patientId)
          .order('date_rdv', { ascending: false }),
        supabase
          .from('prescriptions')
          .select('*, medecins(nom, prenom, specialite)')
          .eq('patient_id', patientId)
          .order('date_prescription', { ascending: false }),
        supabase
          .from('bons_laboratoire')
          .select('*, medecins(nom, prenom, specialite)')
          .eq('patient_id', patientId)
          .order('date_prescription', { ascending: false }),
        supabase
          .from('suivis_patients')
          .select('*, medecins_expediteur:medecin_expediteur_id(nom, prenom, specialite), medecins_destinataire:medecin_destinataire_id(nom, prenom, specialite)')
          .eq('patient_id', patientId)
          .order('created_at', { ascending: false }),
        supabase
          .from('dossiers_roses')
          .select('*')
          .eq('patient_id', patientId)
          .maybeSingle(),
      ]);

      if (consultRes.data) setConsultations(consultRes.data);
      if (prescRes.data) setPrescriptions(prescRes.data);
      if (bonsRes.data) setBonsLabo(bonsRes.data);
      if (suivisRes.data) setSuivis(suivisRes.data);
      if (dossierRes.data) {
        setHasDossierRose(true);
        setDossierRose(dossierRes.data);
      } else {
        setHasDossierRose(false);
        setDossierRose(null);
      }
    } catch (err) {
      console.error('Erreur chargement dossiers:', err);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleSavePatient = async () => {
    if (!formData.nom.trim() || !formData.prenom.trim()) {
      setErrorMsg('Le nom et le prénom sont obligatoires.');
      return;
    }

    // Si c'est un patient temporaire, la date de naissance est obligatoire pour la promotion
    if (selectedTempPatient && !formData.date_naissance) {
      setErrorMsg('La date de naissance est obligatoire pour créer le dossier patient.');
      return;
    }

    // Si c'est une création de nouveau patient, la date de naissance est aussi obligatoire
    if (!selectedPatient && !selectedTempPatient && !formData.date_naissance) {
      setErrorMsg('La date de naissance est obligatoire pour créer un nouveau patient.');
      return;
    }

    // Téléphone ou email obligatoire pour toute création de dossier patient
    if (!selectedPatient && !formData.telephone.trim() && !formData.email.trim()) {
      setErrorMsg('Veuillez définir au moins un numéro de téléphone ou une adresse email au dossier.');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    const payload: Record<string, unknown> = {
      nom: formData.nom.trim().toUpperCase(),
      prenom: formData.prenom.trim(),
      date_naissance: formData.date_naissance || null,
      sexe: formData.sexe || null,
      telephone: formData.telephone || null,
      email: formData.email || null,
      adresse: formData.adresse || null,
      groupe_sanguin: formData.groupe_sanguin || null,
      allergies: formData.allergies || null,
      antecedents_medicaux: formData.antecedents_medicaux || null,
      maladies_chroniques: formData.maladies_chroniques || null,
      antecedents_chirurgicaux: formData.antecedents_chirurgicaux || null,
      medicaments_actuels: formData.medicaments_actuels || null,
      notes_medicales: formData.notes_medicales || null,
      personne_contact_nom: formData.personne_contact_nom || null,
      personne_contact_telephone: formData.personne_contact_telephone || null,
      updated_at: new Date().toISOString(),
    };

    try {
      if (selectedTempPatient) {
        // PROMOTION : patient temporaire → patient reel
        const newPatientData = {
          ...payload,
          numero_dossier: selectedTempPatient.numero_dossier,
          is_provisional: false,
          created_by_staff: true,
          status: 'ACTIVE',
          created_at: new Date().toISOString(),
        };

        const newPatient = await mutate({
          table: 'patients',
          operation: 'INSERT',
          data: newPatientData,
          onlineExecutor: async () => {
            const { data: np, error } = await supabase
              .from('patients')
              .insert(newPatientData)
              .select()
              .single();
            if (error) throw error;
            return np;
          },
          metadata: { summary: `Promotion patient ${selectedTempPatient.nom} ${selectedTempPatient.prenom} (${selectedTempPatient.numero_dossier})` },
        });

        // Supprimer de la table temporaire (avec retry pour éviter les orphelins)
        let deleted = false;
        for (let attempt = 0; attempt < 5; attempt++) {
          const { error: delErr } = await supabase
            .from('patients_temporaires')
            .delete()
            .eq('id', selectedTempPatient.id);

          if (!delErr) {
            // Vérifier que la suppression a bien eu lieu
            const { data: checkData } = await supabase
              .from('patients_temporaires')
              .select('id')
              .eq('id', selectedTempPatient.id)
              .maybeSingle();

            if (!checkData) {
              deleted = true;
              break;
            }
            console.warn(`Tentative ${attempt + 1}: enregistrement temporaire toujours présent, nouvelle tentative...`);
          } else {
            console.error(`Tentative ${attempt + 1}: erreur suppression patient temporaire:`, delErr);
          }

          if (attempt < 4) {
            await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
          }
        }

        if (!deleted) {
          // Nettoyage de secours via edge function avec privilèges admin
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;
            if (token) {
              const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
              const cleanupRes = await fetch(`${supabaseUrl}/functions/v1/delete-patient`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  action: 'cleanup_temp',
                  temp_patient_id: selectedTempPatient.id,
                  temp_numero_dossier: selectedTempPatient.numero_dossier,
                }),
              });
              if (cleanupRes.ok) {
                const cleanupData = await cleanupRes.json();
                if (cleanupData?.deleted) {
                  deleted = true;
                }
              }
            }
          } catch (cleanupErr) {
            console.error('Nettoyage de secours échoué:', cleanupErr);
          }

          // Vérification finale
          if (!deleted) {
            const { data: finalCheck } = await supabase
              .from('patients_temporaires')
              .select('id')
              .eq('id', selectedTempPatient.id)
              .maybeSingle();

            if (finalCheck) {
              setErrorMsg('ATTENTION : Le patient a été créé mais l\'ancien enregistrement temporaire n\'a pas pu être supprimé. Veuillez contacter l\'administrateur pour le nettoyage manuel du dossier ' + selectedTempPatient.numero_dossier + '.');
              setSaving(false);
              return;
            }
          }
        }

        // Retirer ce patient temporaire de la liste de recherche
        setSearchResults((prev) => prev.filter((r) => r._source !== 'temporaire' || r.id !== selectedTempPatient.id));

        setSuccessMsg(`Patient créé avec succès ! Dossier ${selectedTempPatient.numero_dossier}.`);
        setSelectedPatient(newPatient as Patient);
        setSelectedTempPatient(null);
        setConsultations([]);
        setPrescriptions([]);
        setBonsLabo([]);
        setSuivis([]);

        // Générer un code d'activation pour permettre au patient de créer son compte
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const codeRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-patient-activation-code`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ patientId: newPatient.id, numeroDossier: selectedTempPatient.numero_dossier }),
          });
          const codeData = await codeRes.json();
          if (codeData.success && codeData.code) {
            setGeneratedActivationCode(codeData.code);
            setGeneratedNumeroDossier(selectedTempPatient.numero_dossier);
          }
        } catch (codeErr) {
          console.error('Erreur génération code activation:', codeErr);
        }
      } else if (selectedPatient) {
        // MISE À JOUR d'un patient existant
        const updatedPatient = await mutate({
          table: 'patients',
          operation: 'UPDATE',
          recordId: selectedPatient.id,
          data: payload,
          onlineExecutor: async () => {
            const { data: upPatient, error } = await supabase.from('patients').update(payload).eq('id', selectedPatient.id).select().single();
            if (error) throw error;
            return upPatient;
          },
          metadata: { summary: `Mise a jour patient ${payload.nom} ${payload.prenom}` },
        });
        setSuccessMsg('Dossier patient mis a jour avec succes.');
        setSelectedPatient(updatedPatient as Patient);
      } else {
        // CRÉATION d'un nouveau patient — vérifier d'abord les doublons nom+prénom
        const nomUpper = (payload.nom as string).toUpperCase();
        const prenomLower = (payload.prenom as string).toLowerCase();

        // Chercher dans les deux tables en parallèle
        const [realRes, tempRes] = await Promise.all([
          supabase
            .from('patients')
            .select('*')
            .eq('nom', nomUpper)
            .ilike('prenom', prenomLower)
            .limit(10),
          supabase
            .from('patients_temporaires')
            .select('*')
            .eq('nom', nomUpper)
            .ilike('prenom', prenomLower)
            .limit(10),
        ]);

        const allDuplicates: SearchResult[] = [];
        if (realRes.data) {
          for (const p of realRes.data) {
            allDuplicates.push({ ...p, _source: 'patient' as const });
          }
        }
        if (tempRes.data) {
          for (const p of tempRes.data) {
            allDuplicates.push({ ...p, _source: 'temporaire' as const });
          }
        }

        if (allDuplicates.length > 0) {
          // Doublons trouvés → afficher la modale
          setDuplicatePatients(allDuplicates);
          setPendingCreateData(payload);
          setShowDuplicateModal(true);
          setSaving(false);
          return;
        }

        // Aucun doublon → création normale
        await createNewPatientAndAssignDossier(payload);
      }
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const createNewPatientAndAssignDossier = async (payload: Record<string, unknown>) => {
    setSaving(true);
    try {
      // Essayer d'abord la RPC PostgreSQL
      const { data: numeroDossier, error: rpcError } = await supabase.rpc('obtenir_prochain_numero_dossier');
      
      if (rpcError) {
        console.error('Erreur RPC obtenir_prochain_numero_dossier:', rpcError);
        // Fallback: appeler l'edge function
        const { data: edgeData, error: edgeError } = await supabase.functions.invoke('obtenir-prochain-numero-dossier', { body: {} });
        if (edgeError || !edgeData?.success) {
          console.error('Erreur Edge Function:', edgeError || edgeData?.error);
          throw new Error('Impossible d\'attribuer un numéro de dossier.');
        }
        const newDossier = edgeData.numero_dossier as string;
        await insertPatient(payload, newDossier);
        return;
      }
      
      const newDossier = numeroDossier as string;
      await insertPatient(payload, newDossier);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message :
                      (typeof err === 'object' && err !== null && 'message' in err) ? String((err as { message: unknown }).message) :
                      'Erreur lors de la création du patient.';
      console.error('Erreur création patient:', err);
      setErrorMsg(message);
    } finally {
      setSaving(false);
    }
  };

  const insertPatient = async (payload: Record<string, unknown>, newDossier: string) => {
    const patientData = {
      ...payload,
      numero_dossier: newDossier,
      is_provisional: false,
      created_by_staff: true,
      status: 'ACTIVE',
      created_at: new Date().toISOString(),
    };

    try {
      const result = await mutate({
        table: 'patients',
        operation: 'INSERT',
        data: patientData,
        onlineExecutor: async () => {
          const { data: newPatient, error } = await supabase
            .from('patients')
            .insert(patientData)
            .select()
            .single();

          if (error) {
            console.error('Erreur INSERT patient:', error);
            const errorMsg = (error.message || '').toLowerCase();
            const isPermissionError = errorMsg.includes('row-level security')
              || errorMsg.includes('policy')
              || errorMsg.includes('access denied')
              || errorMsg.includes('permission denied')
              || errorMsg.includes('super_admin');

            if (isPermissionError) {
              console.log('Erreur de permission detectee, tentative via Edge Function create-patient-by-staff...');
              const { data: sessionData } = await supabase.auth.getSession();
              const token = sessionData?.session?.access_token;
              if (token) {
                const edgeRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-patient-by-staff`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({
                    nom: payload.nom,
                    prenom: payload.prenom,
                    dateNaissance: payload.date_naissance,
                    email: payload.email,
                    telephone: payload.telephone,
                    adresse: payload.adresse,
                    sexe: payload.sexe,
                    password: 'Patient123!',
                  }),
                });
                const edgeResult = await edgeRes.json();
                if (edgeRes.ok && edgeResult.success) {
                  return edgeResult.patient;
                }
                console.error('Edge function fallback echoue:', edgeResult);
                throw new Error(edgeResult?.error || 'Erreur lors de la creation du patient via le serveur.');
              }
            }
            throw new Error(error.message || 'Erreur lors de l\'insertion du patient.');
          }
          return newPatient;
        },
        metadata: { summary: `Creation patient ${payload.nom} ${payload.prenom} (${newDossier})` },
      });

      setSuccessMsg(`Patient cree avec le dossier ${newDossier}.`);
      setIsCreatingNew(false);
      setSelectedPatient(result as Patient);
      setConsultations([]);
      setPrescriptions([]);
      setBonsLabo([]);
      setSuivis([]);

      // Générer un code d'activation pour permettre au patient de créer son compte
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const codeRes = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-patient-activation-code`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ patientId: result.id, numeroDossier: newDossier }),
        });
        const codeData = await codeRes.json();
        if (codeData.success && codeData.code) {
          setGeneratedActivationCode(codeData.code);
          setGeneratedNumeroDossier(newDossier);
        }
      } catch (codeErr) {
        console.error('Erreur génération code activation:', codeErr);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message :
                      (typeof err === 'object' && err !== null && 'message' in err) ? String((err as { message: unknown }).message) :
                      'Erreur lors de la creation du patient.';
      console.error('Erreur creation patient:', err);
      setErrorMsg(message);
      throw err;
    }
  };

  const confirmSelectDuplicate = (result: SearchResult) => {
    setShowDuplicateModal(false);
    setDuplicatePatients([]);
    setPendingCreateData(null);
    setErrorMsg('');
    selectPatient(result);
  };

  const confirmNotDuplicate = () => {
    setShowDuplicateModal(false);
    setDuplicatePatients([]);
    setPendingCreateData(null);
    setErrorMsg('Un ou plusieurs patients portent déjà ce nom et prénom. Si cette personne est différente, veuillez utiliser un second prénom ou contacter l\'administrateur pour obtenir une autorisation de création.');
  };

  // ... existing code ...
  const handleAddConsultation = async (data: ConsultationFormData) => {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      await mutate({
        table: 'rendez_vous',
        operation: 'INSERT',
        data: {
          patient_id: selectedPatient.id,
          medecin_id: data.medecin_id,
          date_rdv: data.date_rdv,
          heure_debut: data.heure_debut,
          heure_fin: data.heure_fin,
          statut: 'termine',
          motif: data.motif,
          diagnostique: data.diagnostique,
          traitement: data.traitement,
          medicaments: data.medicaments,
          notes_medecin: data.notes_medecin,
          type_consultation: data.type_consultation,
        },
        onlineExecutor: async () => {
          const { data: result, error } = await supabase.from('rendez_vous').insert({
            patient_id: selectedPatient.id,
            medecin_id: data.medecin_id,
            date_rdv: data.date_rdv,
            heure_debut: data.heure_debut,
            heure_fin: data.heure_fin,
            statut: 'termine',
            motif: data.motif,
            diagnostique: data.diagnostique,
            traitement: data.traitement,
            medicaments: data.medicaments,
            notes_medecin: data.notes_medecin,
            type_consultation: data.type_consultation,
          }).select().single();
          if (error) throw error;
          return result;
        },
        metadata: { summary: `Consultation ${selectedPatient.nom} ${selectedPatient.prenom}` },
      });
      setShowConsultForm(false);
      setSuccessMsg('Consultation anterieure ajoutee.');
      await loadAllPatientRecords(selectedPatient.id);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'ajout de la consultation.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPrescription = async (data: PrescriptionFormData) => {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      const medicamentsJson = data.medicaments
        ? [{ nom: data.medicaments, posologie: '' }]
        : [];
      await mutate({
        table: 'prescriptions',
        operation: 'INSERT',
        data: {
          patient_id: selectedPatient.id,
          medecin_id: data.medecin_id,
          date_prescription: data.date_prescription,
          diagnostic: data.diagnostic,
          medicaments: medicamentsJson,
          instructions: data.instructions,
          duree_traitement: data.duree_traitement,
        },
        onlineExecutor: async () => {
          const { data: result, error } = await supabase.from('prescriptions').insert({
            patient_id: selectedPatient.id,
            medecin_id: data.medecin_id,
            date_prescription: data.date_prescription,
            diagnostic: data.diagnostic,
            medicaments: medicamentsJson,
            instructions: data.instructions,
            duree_traitement: data.duree_traitement,
          }).select().single();
          if (error) throw error;
          return result;
        },
        metadata: { summary: `Prescription ${selectedPatient.nom} ${selectedPatient.prenom}` },
      });
      setShowPrescriptionForm(false);
      setSuccessMsg('Prescription anterieure ajoutee.');
      await loadAllPatientRecords(selectedPatient.id);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'ajout de la prescription.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBonLabo = async (data: BonLaboratoireFormData) => {
    if (!selectedPatient) return;
    setSaving(true);
    try {
      await mutate({
        table: 'bons_laboratoire',
        operation: 'INSERT',
        data: {
          patient_id: selectedPatient.id,
          medecin_id: data.medecin_id,
          date_prescription: data.date_prescription,
          type_analyse: data.type_analyse,
          description: data.description,
          observations_cliniques: data.observations_cliniques,
          urgences: data.urgence,
          statut: 'en_attente',
        },
        onlineExecutor: async () => {
          const { data: result, error } = await supabase.from('bons_laboratoire').insert({
            patient_id: selectedPatient.id,
            medecin_id: data.medecin_id,
            date_prescription: data.date_prescription,
            type_analyse: data.type_analyse,
            description: data.description,
            observations_cliniques: data.observations_cliniques,
            urgences: data.urgence,
            statut: 'en_attente',
          }).select().single();
          if (error) throw error;
          return result;
        },
        metadata: { summary: `Bon labo ${selectedPatient.nom} ${selectedPatient.prenom}` },
      });
      setShowBonLaboForm(false);
      setSuccessMsg('Bon de laboratoire anterieur ajoute.');
      await loadAllPatientRecords(selectedPatient.id);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'ajout du bon de laboratoire.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!selectedTempPatient) return;
    if (!correctionNom.trim() || !correctionPrenom.trim()) {
      setErrorMsg('Le nom et le prénom corrigés sont obligatoires.');
      return;
    }
    setSubmittingCorrection(true);
    setErrorMsg('');
    try {
      await mutate({
        table: 'corrections_temporaires',
        operation: 'INSERT',
        data: {
          patient_temp_id: selectedTempPatient.id,
          ancien_nom: selectedTempPatient.nom,
          ancien_prenom: selectedTempPatient.prenom,
          nouveau_nom: correctionNom.trim().toUpperCase(),
          nouveau_prenom: correctionPrenom.trim(),
          demandeur_id: user?.id,
          statut: 'en_attente',
        },
        onlineExecutor: async () => {
          const { data: result, error } = await supabase.from('corrections_temporaires').insert({
            patient_temp_id: selectedTempPatient.id,
            ancien_nom: selectedTempPatient.nom,
            ancien_prenom: selectedTempPatient.prenom,
            nouveau_nom: correctionNom.trim().toUpperCase(),
            nouveau_prenom: correctionPrenom.trim(),
            demandeur_id: user?.id,
            statut: 'en_attente',
          }).select().single();
          if (error) throw error;
          return result;
        },
        metadata: { summary: `Correction nom ${selectedTempPatient.nom} ${selectedTempPatient.prenom}` },
      });
      setSuccessMsg(`Demande de correction envoyee a l'administrateur : ${correctionNom.toUpperCase()} ${correctionPrenom}.`);
      setShowCorrectionForm(false);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur lors de l\'envoi de la demande de correction.');
    } finally {
      setSubmittingCorrection(false);
    }
  };

  const formatMedicaments = (med: unknown): string => {
    if (!med) return '';
    if (typeof med === 'string') return med;
    if (Array.isArray(med)) {
      return med.map((m: Record<string, unknown>) => m.nom || '').filter(Boolean).join(', ');
    }
    return String(med);
  };

  const statutBonLabo = (statut: string): { label: string; color: string } => {
    switch (statut) {
      case 'en_attente': return { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' };
      case 'en_cours': return { label: 'En cours', color: 'bg-blue-100 text-blue-700' };
      case 'termine': return { label: 'Terminé', color: 'bg-green-100 text-green-700' };
      default: return { label: statut || 'En attente', color: 'bg-gray-100 text-gray-600' };
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4"></i>
          <div className="text-clinic-blue-dark text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  const PATIENT_TABS: { key: PatientTab; label: string; icon: string; count?: number }[] = [
    { key: 'infos', label: 'Infos patient', icon: 'ri-user-line' },
    { key: 'consultations', label: 'Consultations', icon: 'ri-stethoscope-line', count: consultations.length },
    { key: 'prescriptions', label: 'Prescriptions', icon: 'ri-medicine-bottle-line', count: prescriptions.length },
    { key: 'bons-labos', label: 'Bons labo', icon: 'ri-microscope-line', count: bonsLabo.length },
    { key: 'suivis', label: 'Suivis', icon: 'ri-chat-check-line', count: suivis.length },
    { key: 'dossier-rose', label: 'Dossier Rose', icon: 'ri-folder-user-line', count: hasDossierRose ? 1 : 0 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Patients</h1>
              <p className="mt-2 text-gray-600">Recherchez et gérez les dossiers patients complets</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setSelectedPatient(null); setSelectedTempPatient(null); setIsCreatingNew(true); setFormData(EMPTY_FORM); setConsultations([]); setPrescriptions([]); setBonsLabo([]); setSuivis([]); setHasDossierRose(false); setDossierRose(null); setErrorMsg(''); setSuccessMsg(''); }}
                className="flex items-center gap-2 px-5 py-2.5 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line"></i>
                Nouveau patient
              </button>
              <button
                onClick={() => navigate('/secretaire/dashboard')}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{errorMsg}</span>
          </div>
        )}
        {successMsg && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl flex items-start gap-2">
            <i className="ri-check-line mt-0.5 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm">{successMsg}</span>
          </div>
        )}

        {/* Recherche + liste patients (toujours visible) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Colonne gauche : recherche + liste */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200">
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchPatients()}
                  placeholder="Nom, prénom ou N° dossier..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-loader-4-line animate-spin text-2xl"></i>
                  <p className="text-sm mt-2">Recherche...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <i className="ri-user-search-line text-3xl text-gray-300 mb-3 block"></i>
                  <p className="text-sm">Recherchez un patient</p>
                </div>
              ) : (
                searchResults.map((p) => (
                  <div
                    key={`${p._source}-${p.id}`}
                    onClick={() => selectPatient(p)}
                    className={`p-4 cursor-pointer transition-all border-l-4 ${
                      (p._source === 'patient' && selectedPatient?.id === p.id) || (p._source === 'temporaire' && selectedTempPatient?.id === p.id)
                        ? 'bg-clinic-blue/5 border-clinic-blue'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-gray-900 text-sm">{p.nom} {p.prenom}</p>
                      {p._source === 'temporaire' && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">À compléter</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {p.numero_dossier && (
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded-md font-mono">{p.numero_dossier}</span>
                      )}
                      {p._source === 'patient' && (p as Patient).date_naissance && <span>Né(e) {new Date((p as Patient).date_naissance).toLocaleDateString('fr-FR')}</span>}
                      {p._source === 'temporaire' && (
                        <span className="text-amber-600">Importé le {new Date((p as PatientTemporaire).created_at).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Colonne droite : dossier patient */}
          <div className="lg:col-span-3">
            {/* PATIENT TEMPORAIRE - Formulaire de complétion */}
            {selectedTempPatient && !selectedPatient && (
              <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
                <div className="p-6 border-b border-amber-100 bg-amber-50/50">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-add-line text-2xl text-amber-600"></i>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-gray-900">
                          {selectedTempPatient.nom} {selectedTempPatient.prenom}
                        </h2>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Patient à compléter</span>
                        {!showCorrectionForm && (
                          <button
                            onClick={() => {
                              setCorrectionNom(selectedTempPatient.nom);
                              setCorrectionPrenom(selectedTempPatient.prenom);
                              setShowCorrectionForm(true);
                              setErrorMsg('');
                              setSuccessMsg('');
                            }}
                            className="px-2 py-0.5 bg-white border border-amber-300 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors text-xs font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
                            title="Demander une correction de nom"
                          >
                            <i className="ri-edit-line w-3 h-3 flex items-center justify-center"></i>
                            Corriger le nom
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-amber-700">
                        <i className="ri-information-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                        Ce patient a été importé depuis un fichier CSV. Veuillez compléter ses informations pour créer son dossier définitif.
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="px-2 py-0.5 bg-white rounded-md font-mono text-xs text-gray-600 border border-gray-200">
                          {selectedTempPatient.numero_dossier}
                        </span>
                        <span className="text-xs text-gray-500">
                          Importé le {new Date(selectedTempPatient.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                    <button
                      onClick={handleSavePatient}
                      disabled={saving}
                      className="px-5 py-2.5 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      {saving ? <><i className="ri-loader-4-line animate-spin"></i>Création...</> : <><i className="ri-user-add-line"></i>Créer le dossier</>}
                    </button>
                    {showCorrectionForm && (
                      <button
                        onClick={() => setShowCorrectionForm(false)}
                        className="px-3 py-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
                      </button>
                    )}
                    </div>
                  </div>
                </div>

                {/* Formulaire de correction */}
                {showCorrectionForm && (
                  <div className="px-6 pt-4 pb-0 bg-white border-b border-amber-100">
                    <div className="p-4 bg-white border border-amber-300 rounded-xl">
                      <p className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                        <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                        Demande de correction du nom
                      </p>
                      <p className="text-xs text-amber-600 mb-4">
                        Cette correction sera soumise à l'administrateur pour validation.
                      </p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Nom corrigé</label>
                          <input
                            type="text"
                            value={correctionNom}
                            onChange={(e) => setCorrectionNom(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Prénom corrigé</label>
                          <input
                            type="text"
                            value={correctionPrenom}
                            onChange={(e) => setCorrectionPrenom(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-amber-400"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleSubmitCorrection}
                          disabled={submittingCorrection}
                          className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                        >
                          {submittingCorrection ? (
                            <><i className="ri-loader-4-line animate-spin w-3.5 h-3.5 flex items-center justify-center"></i>Envoi...</>
                          ) : (
                            <><i className="ri-send-plane-line w-3.5 h-3.5 flex items-center justify-center"></i>Envoyer la demande</>
                          )}
                        </button>
                        <button
                          onClick={() => setShowCorrectionForm(false)}
                          className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-6">
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                      <i className="ri-alert-line w-4 h-4 flex items-center justify-center"></i>
                      La date de naissance est obligatoire pour créer le dossier patient.
                    </p>
                  </div>
                  <PatientForm
                    initialData={formData}
                    onChange={setFormData}
                    readOnlyFields={[]}
                  />
                </div>
              </div>
            )}

            {/* PATIENT RÉEL */}
            {selectedPatient && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* En-tête patient */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedPatient.nom} {selectedPatient.prenom}
                      </h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Dossier {selectedPatient.numero_dossier || 'N/A'}
                        {selectedPatient.is_provisional && (
                          <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Provisoire</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={handleSavePatient}
                      disabled={saving}
                      className="px-5 py-2.5 bg-clinic-blue-dark text-white rounded-xl hover:bg-clinic-blue transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
                    >
                      {saving ? <><i className="ri-loader-4-line animate-spin"></i>Sauvegarde...</> : <><i className="ri-save-line"></i>Enregistrer</>}
                    </button>
                  </div>

                  {/* Onglets */}
                  <div className="flex gap-1 mt-5 bg-gray-100 p-1 rounded-xl overflow-x-auto">
                    {PATIENT_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                          activeTab === tab.key
                            ? 'bg-white text-clinic-blue-dark shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <i className={`${tab.icon} w-4 h-4 flex items-center justify-center`}></i>
                        {tab.label}
                        {tab.count !== undefined && tab.count > 0 && (
                          <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                            activeTab === tab.key ? 'bg-clinic-blue text-white' : 'bg-gray-300 text-gray-700'
                          }`}>
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contenu des onglets */}
                <div className="p-6">
                  {loadingRecords && (
                    <div className="text-center py-8">
                      <i className="ri-loader-4-line animate-spin text-2xl text-clinic-blue"></i>
                      <p className="text-sm text-gray-500 mt-2">Chargement des dossiers...</p>
                    </div>
                  )}

                  {/* INFOS PATIENT */}
                  {activeTab === 'infos' && !loadingRecords && (
                    <PatientForm
                      initialData={formData}
                      onChange={setFormData}
                      readOnlyFields={[]}
                    />
                  )}

                  {/* CONSULTATIONS */}
                  {activeTab === 'consultations' && !loadingRecords && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2">
                          <i className="ri-stethoscope-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
                          Consultations ({consultations.length})
                        </h3>
                        <button
                          onClick={() => setShowConsultForm(!showConsultForm)}
                          className="px-4 py-2 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-medium text-sm whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                        >
                          <i className={showConsultForm ? 'ri-close-line' : 'ri-add-line'}></i>
                          {showConsultForm ? 'Annuler' : 'Ajouter une consultation'}
                        </button>
                      </div>

                      {showConsultForm && (
                        <div className="mb-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
                          <ConsultationForm
                            medecins={medecins}
                            onSubmit={handleAddConsultation}
                            onCancel={() => setShowConsultForm(false)}
                            loading={saving}
                          />
                        </div>
                      )}

                      {consultations.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <i className="ri-calendar-line text-3xl text-gray-300 mb-2 block"></i>
                          <p className="text-sm">Aucune consultation enregistrée</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {consultations.map((c) => (
                            <div key={c.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">
                                    {new Date(c.date_rdv).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Dr. {c.medecins?.nom} {c.medecins?.prenom}
                                    {c.type_consultation && <span className="ml-2">- {c.type_consultation}</span>}
                                  </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  c.statut === 'termine' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {c.statut === 'termine' ? 'Terminé' : c.statut}
                                </span>
                              </div>
                              {c.motif && <p className="text-sm text-gray-700 mb-1"><strong>Motif :</strong> {c.motif}</p>}
                              {c.diagnostique && <p className="text-sm text-gray-700 mb-1"><strong>Diagnostic :</strong> {c.diagnostique}</p>}
                              {c.traitement && <p className="text-sm text-gray-700 mb-1"><strong>Traitement :</strong> {c.traitement}</p>}
                              {c.medicaments && <p className="text-sm text-gray-700"><strong>Médicaments :</strong> {c.medicaments}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* PRESCRIPTIONS */}
                  {activeTab === 'prescriptions' && !loadingRecords && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2">
                          <i className="ri-medicine-bottle-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
                          Prescriptions ({prescriptions.length})
                        </h3>
                        <button
                          onClick={() => setShowPrescriptionForm(!showPrescriptionForm)}
                          className="px-4 py-2 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-medium text-sm whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                        >
                          <i className={showPrescriptionForm ? 'ri-close-line' : 'ri-add-line'}></i>
                          {showPrescriptionForm ? 'Annuler' : 'Ajouter une prescription'}
                        </button>
                      </div>

                      {showPrescriptionForm && (
                        <div className="mb-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
                          <PrescriptionForm
                            medecins={medecins}
                            onSubmit={handleAddPrescription}
                            onCancel={() => setShowPrescriptionForm(false)}
                            loading={saving}
                          />
                        </div>
                      )}

                      {prescriptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <i className="ri-medicine-bottle-line text-3xl text-gray-300 mb-2 block"></i>
                          <p className="text-sm">Aucune prescription enregistrée</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {prescriptions.map((p) => (
                            <div key={p.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">
                                    {p.date_prescription
                                      ? new Date(p.date_prescription).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                                      : 'Date inconnue'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Dr. {p.medecins?.nom} {p.medecins?.prenom}
                                  </p>
                                </div>
                                {p.duree_traitement && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                    {p.duree_traitement}
                                  </span>
                                )}
                              </div>
                              {p.diagnostic && <p className="text-sm text-gray-700 mb-1"><strong>Diagnostic :</strong> {p.diagnostic}</p>}
                              <p className="text-sm text-gray-700 mb-1">
                                <strong>Médicaments :</strong> {formatMedicaments(p.medicaments) || 'Non spécifiés'}
                              </p>
                              {p.instructions && <p className="text-sm text-gray-700"><strong>Instructions :</strong> {p.instructions}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* BONS LABORATOIRE */}
                  {activeTab === 'bons-labos' && !loadingRecords && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2">
                          <i className="ri-microscope-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
                          Bons de laboratoire ({bonsLabo.length})
                        </h3>
                        <button
                          onClick={() => setShowBonLaboForm(!showBonLaboForm)}
                          className="px-4 py-2 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-medium text-sm whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                        >
                          <i className={showBonLaboForm ? 'ri-close-line' : 'ri-add-line'}></i>
                          {showBonLaboForm ? 'Annuler' : 'Ajouter un bon'}
                        </button>
                      </div>

                      {showBonLaboForm && (
                        <div className="mb-6 p-5 bg-gray-50 rounded-2xl border border-gray-200">
                          <BonLaboratoireForm
                            medecins={medecins}
                            onSubmit={handleAddBonLabo}
                            onCancel={() => setShowBonLaboForm(false)}
                            loading={saving}
                          />
                        </div>
                      )}

                      {bonsLabo.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <i className="ri-microscope-line text-3xl text-gray-300 mb-2 block"></i>
                          <p className="text-sm">Aucun bon de laboratoire enregistré</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {bonsLabo.map((b) => {
                            const s = statutBonLabo(b.statut);
                            return (
                              <div key={b.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <p className="font-semibold text-gray-900 text-sm">
                                      {b.date_prescription
                                        ? new Date(b.date_prescription).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                                        : 'Date inconnue'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      Dr. {b.medecins?.nom} {b.medecins?.prenom}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {b.urgences && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                        Urgent
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
                                      {s.label}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-sm text-gray-700 mb-1">
                                  <strong>{b.type_analyse}</strong>
                                </p>
                                {b.description && <p className="text-sm text-gray-600 mb-1">{b.description}</p>}
                                {b.observations_cliniques && <p className="text-xs text-gray-500"><strong>Obs. :</strong> {b.observations_cliniques}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* SUIVIS */}
                  {activeTab === 'suivis' && !loadingRecords && (
                    <div>
                      <h3 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2 mb-4">
                        <i className="ri-chat-check-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
                        Suivis ({suivis.length})
                      </h3>

                      {suivis.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <i className="ri-chat-check-line text-3xl text-gray-300 mb-2 block"></i>
                          <p className="text-sm">Aucun suivi enregistré</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {suivis.map((s) => (
                            <div key={s.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{s.objet}</p>
                                  <p className="text-xs text-gray-500">
                                    {s.created_at
                                      ? new Date(s.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                                      : ''}
                                    {' - '}
                                    De Dr. {s.medecins_expediteur?.nom} {s.medecins_expediteur?.prenom}
                                    {' → '}
                                    Dr. {s.medecins_destinataire?.nom} {s.medecins_destinataire?.prenom}
                                  </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  s.statut === 'lu' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {s.statut === 'lu' ? 'Lu' : 'Non lu'}
                                </span>
                              </div>
                              {s.message && <p className="text-sm text-gray-700 mb-1">{s.message}</p>}
                              {s.diagnostic && <p className="text-sm text-gray-700 mb-1"><strong>Diagnostic :</strong> {s.diagnostic}</p>}
                              {s.traitement && <p className="text-sm text-gray-700 mb-1"><strong>Traitement :</strong> {s.traitement}</p>}
                              {s.notes_medicales && <p className="text-sm text-gray-600"><strong>Notes :</strong> {s.notes_medicales}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* DOSSIER ROSE */}
                  {activeTab === 'dossier-rose' && !loadingRecords && (
                    <div>
                      <h3 className="text-lg font-bold text-clinic-blue-dark flex items-center gap-2 mb-4">
                        <i className="ri-folder-user-line text-clinic-blue w-5 h-5 flex items-center justify-center"></i>
                        Dossier Rose
                      </h3>

                      {!hasDossierRose ? (
                        <div className="text-center py-8 text-gray-500">
                          <i className="ri-folder-user-line text-3xl text-gray-300 mb-2 block"></i>
                          <p className="text-sm">Aucun dossier rose pour ce patient</p>
                        </div>
                      ) : dossierRose && (
                        <div className="space-y-6">
                          {/* Infos administratives */}
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                              <i className="ri-file-list-3-line text-clinic-blue w-4 h-4 flex items-center justify-center"></i>
                              Informations administratives
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div><span className="text-gray-500">N° Dossier :</span> <span className="font-medium">{String(dossierRose.no_dossier || '-')}</span></div>
                              <div><span className="text-gray-500">N° Accouchement :</span> <span className="font-medium">{String(dossierRose.no_accouchement || '-')}</span></div>
                              <div><span className="text-gray-500">Année :</span> <span className="font-medium">{String(dossierRose.annee || '-')}</span></div>
                              <div><span className="text-gray-500">Date accouchement :</span> <span className="font-medium">{dossierRose.date_accouchement ? new Date(String(dossierRose.date_accouchement)).toLocaleDateString('fr-FR') : '-'}</span></div>
                              <div><span className="text-gray-500">Structure :</span> <span className="font-medium">{String(dossierRose.structure_sanitaire || '-')}</span></div>
                              <div><span className="text-gray-500">Département :</span> <span className="font-medium">{String(dossierRose.departement || '-')}</span></div>
                              <div><span className="text-gray-500">Commune :</span> <span className="font-medium">{String(dossierRose.commune || '-')}</span></div>
                              <div><span className="text-gray-500">Zone sanitaire :</span> <span className="font-medium">{String(dossierRose.zone_sanitaire || '-')}</span></div>
                            </div>
                          </div>

                          {/* Grossesses */}
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                              <i className="ri-heart-pulse-line text-clinic-blue w-4 h-4 flex items-center justify-center"></i>
                              Antécédents obstétricaux
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                              <div><span className="text-gray-500">Gestité :</span> <span className="font-medium">{String(dossierRose.gestite ?? '-')}</span></div>
                              <div><span className="text-gray-500">Parité :</span> <span className="font-medium">{String(dossierRose.parite ?? '-')}</span></div>
                              <div><span className="text-gray-500">Avortements :</span> <span className="font-medium">{String(dossierRose.nombre_avortements ?? '-')}</span></div>
                              <div><span className="text-gray-500">Enfants vivants :</span> <span className="font-medium">{String(dossierRose.nombre_enfants_vivants ?? '-')}</span></div>
                              <div><span className="text-gray-500">Enfants décédés :</span> <span className="font-medium">{String(dossierRose.nombre_enfants_decede ?? '-')}</span></div>
                              <div><span className="text-gray-500">DDR :</span> <span className="font-medium">{dossierRose.ddr ? new Date(String(dossierRose.ddr)).toLocaleDateString('fr-FR') : '-'}</span></div>
                              <div><span className="text-gray-500">DPA :</span> <span className="font-medium">{dossierRose.dpa ? new Date(String(dossierRose.dpa)).toLocaleDateString('fr-FR') : '-'}</span></div>
                            </div>
                          </div>

                          {/* Examens */}
                          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <h4 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                              <i className="ri-test-tube-line text-clinic-blue w-4 h-4 flex items-center justify-center"></i>
                              Examens biologiques
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div><span className="text-gray-500">GS/Rh :</span> <span className="font-medium">{String(dossierRose.gs || '-')}/{String(dossierRose.rh || '-')}</span></div>
                              <div><span className="text-gray-500">Taux Hb :</span> <span className="font-medium">{String(dossierRose.taux_hemoglobine || '-')}</span></div>
                              <div><span className="text-gray-500">Hématocrite :</span> <span className="font-medium">{String(dossierRose.hematocrite || '-')}</span></div>
                              <div><span className="text-gray-500">Plaquettes :</span> <span className="font-medium">{String(dossierRose.plaquettes || '-')}</span></div>
                              <div><span className="text-gray-500">Glycémie :</span> <span className="font-medium">{String(dossierRose.glycemie_jeun || '-')}</span></div>
                              <div><span className="text-gray-500">ECBU :</span> <span className="font-medium">{String(dossierRose.ecbu || '-')}</span></div>
                              <div><span className="text-gray-500">VIH1 :</span> <span className="font-medium">{String(dossierRose.hiv1 || '-')}</span></div>
                              <div><span className="text-gray-500">VIH2 :</span> <span className="font-medium">{String(dossierRose.hiv2 || '-')}</span></div>
                              <div><span className="text-gray-500">Hépatite B :</span> <span className="font-medium">{String(dossierRose.hepatite_b || '-')}</span></div>
                              <div><span className="text-gray-500">TPHA :</span> <span className="font-medium">{String(dossierRose.tpha || '-')}</span></div>
                              <div><span className="text-gray-500">VDRL :</span> <span className="font-medium">{String(dossierRose.vdrl || '-')}</span></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* MODE CRÉATION NOUVEAU PATIENT */}
            {isCreatingNew && !selectedPatient && !selectedTempPatient && (
              <div className="bg-white rounded-2xl shadow-sm border border-green-200 overflow-hidden">
                <div className="p-6 border-b border-green-100 bg-green-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-clinic-green/10 rounded-2xl flex items-center justify-center">
                        <i className="ri-user-add-line text-xl text-clinic-green"></i>
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">Nouveau patient</h2>
                        <p className="text-sm text-gray-500">Remplissez les informations pour créer un dossier</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSavePatient}
                        disabled={saving}
                        className="px-5 py-2.5 bg-clinic-green text-white rounded-xl hover:bg-clinic-green/90 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
                      >
                        {saving ? <><i className="ri-loader-4-line animate-spin"></i>Création...</> : <><i className="ri-user-add-line"></i>Créer le patient</>}
                      </button>
                      <button
                        onClick={() => { setIsCreatingNew(false); setErrorMsg(''); setSuccessMsg(''); }}
                        className="px-3 py-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        title="Annuler"
                      >
                        <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <PatientForm
                    initialData={formData}
                    onChange={setFormData}
                    readOnlyFields={[]}
                  />
                </div>
              </div>
            )}

            {!selectedPatient && !selectedTempPatient && !isCreatingNew && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                <div className="w-20 h-20 bg-clinic-blue/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-search-line text-3xl text-clinic-blue"></i>
                </div>
                <p className="text-lg font-medium text-gray-700 mb-1">Sélectionnez un patient</p>
                <p className="text-sm">Recherchez et cliquez sur un patient pour voir et gérer son dossier complet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* MODALE DOUBLONS */}
      {showDuplicateModal && duplicatePatients.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Doublon potentiel détecté</h3>
                  <p className="text-sm text-gray-500">
                    {duplicatePatients.length} patient{duplicatePatients.length > 1 ? 's' : ''} avec le même nom et prénom existe{duplicatePatients.length > 1 ? 'nt' : ''} déjà.
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  <i className="ri-information-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                  S'il s'agit de la même personne, cliquez sur son dossier pour y accéder. Sinon, vous devrez contacter un administrateur.
                </p>
              </div>

              <div className="mt-4 space-y-2">
                {duplicatePatients.map((dp) => (
                  <div
                    key={`${dp._source}-${dp.id}`}
                    className="p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-clinic-blue/5 hover:border-clinic-blue/30 cursor-pointer transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => confirmSelectDuplicate(dp)}
                        className="flex-1 text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 text-sm">
                            {dp.nom} {dp.prenom}
                          </p>
                          {dp._source === 'temporaire' && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">À compléter</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          {dp.numero_dossier && (
                            <span className="px-1.5 py-0.5 bg-gray-200 rounded-md font-mono">{dp.numero_dossier}</span>
                          )}
                          {dp._source === 'patient' && (dp as Patient).date_naissance && <span>Né(e) {new Date((dp as Patient).date_naissance).toLocaleDateString('fr-FR')}</span>}
                          {dp._source === 'patient' && (dp as Patient).telephone && <span>{(dp as Patient).telephone}</span>}
                          {dp._source === 'temporaire' && (
                            <span className="text-amber-600">Importé le {new Date((dp as PatientTemporaire).created_at).toLocaleDateString('fr-FR')}</span>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); confirmSelectDuplicate(dp); }}
                        className="ml-3 px-3 py-1.5 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-all text-xs font-medium whitespace-nowrap cursor-pointer"
                      >
                        C'est lui/elle
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <button
                  onClick={confirmNotDuplicate}
                  className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  Ce n'est pas la même personne
                </button>
                <button
                  onClick={() => { setShowDuplicateModal(false); setDuplicatePatients([]); setPendingCreateData(null); }}
                  className="px-4 py-2.5 text-gray-500 hover:text-gray-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE CODE D'ACTIVATION */}
      {generatedActivationCode && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-clinic-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-key-2-line text-3xl text-clinic-green"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Code d'activation généré</h3>
              <p className="text-sm text-gray-600">
                Remettez ce code au patient. Il lui servira à créer son compte et accéder à son dossier médical.
              </p>
            </div>
            <div className="bg-gray-50 border-2 border-dashed border-clinic-green/40 rounded-2xl p-5 text-center mb-4">
              <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Code d'activation (valable 7 jours)</p>
              <p className="text-2xl font-mono font-bold text-clinic-blue-dark tracking-widest">{generatedActivationCode}</p>
            </div>
            {generatedNumeroDossier && (
              <p className="text-xs text-gray-500 mb-6 text-center">
                <i className="ri-information-line mr-1"></i>
                Dossier <span className="font-mono font-semibold">{generatedNumeroDossier}</span> — le patient saisira ce code + son numéro de dossier.
              </p>
            )}
            <button
              onClick={() => { setGeneratedActivationCode(null); setGeneratedNumeroDossier(null); }}
              className="w-full px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold whitespace-nowrap cursor-pointer"
            >
              J'ai remis le code au patient
            </button>
          </div>
        </div>
      )}
    </div>
  );
}